import { styled } from '@mui/material/styles';
import ContactsTable from '@/pages/PartnerAdminPage/ContactsTable';
import { ContactSchema } from '@/lib/types';
import {
  TableData,
  RowErrors,
} from '@/components/Table/TableHelpers';
import { FormattedMessage } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import { ImportResult } from './ContactsImportModal';
import CheckCircleIcon from '@/assets/check-circle.svg?react';
import WarningTriangleIcon from '@/assets/warning-triangle.svg?react';

interface PreviewPhaseProps {
  contacts: ContactSchema[];
  onPermissionChange: (
    id: number,
    names: string[],
    value: TableData,
  ) => void;
  onDelete: (id: number) => void;
  onEmailChange: (id: number, value: TableData) => void;
  onNameChange: (id: number, value: TableData) => void;
  errors?: RowErrors<ContactSchema>;
  loading?: boolean;
  importResult?: ImportResult | null;
}

const PreviewPhase = ({
  contacts,
  onPermissionChange,
  onDelete,
  onEmailChange,
  onNameChange,
  errors,
  loading,
  importResult,
}: PreviewPhaseProps) => {
  const allSuccess = !!(importResult && importResult.failed === 0);
  const shouldShowTable = contacts.length > 0;

  return (
    <Container>
      <Description>
        <FormattedMessage
          id="previewContactsDescription"
          defaultMessage="Please review the below contacts list"
        />
      </Description>
      {importResult && (
        <ImportResultBanner $allSuccess={allSuccess}>
          {allSuccess ? <SuccessIcon /> : <WarningIcon />}
          <ResultText>
            {allSuccess ? (
              <FormattedMessage
                id="importSuccess"
                defaultMessage="Successfully imported {count} contact{plural}"
                values={{
                  count: importResult.success,
                  plural: importResult.success > 1 ? 's' : '',
                }}
              />
            ) : (
              <FormattedMessage
                id="importPartialSuccess"
                defaultMessage="Imported {success} of {total} contacts. {failed} failed."
                values={{
                  success: importResult.success,
                  total: importResult.total,
                  failed: importResult.failed,
                }}
              />
            )}
          </ResultText>
        </ImportResultBanner>
      )}
      {shouldShowTable && (
        <TableWrapper>
          <ContactsTable
            rows={contacts}
            onPermissionChange={onPermissionChange}
            onDelete={onDelete}
            onEmailChange={onEmailChange}
            onNameChange={onNameChange}
            errors={errors}
          />
          {loading && (
            <LoaderOverlay>
              <LoaderWrapper
                role="status"
                aria-label="Importing contacts"
              >
                <CircularProgress />
              </LoaderWrapper>
            </LoaderOverlay>
          )}
        </TableWrapper>
      )}
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));

const Description = styled('p')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  margin: 0,
  marginBottom: theme.spacing(2),
  color: theme.palette.textPrimaryBlack,
}));

const TableWrapper = styled('div')({
  position: 'relative',
});

const LoaderOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 10,
});

const LoaderWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ImportResultBanner = styled('div')<{
  $allSuccess: boolean;
}>(({ theme, $allSuccess }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: $allSuccess ? theme.spacing(4) : theme.spacing(2),
  borderRadius: theme.radii.xs,
  backgroundColor: $allSuccess
    ? theme.palette.green50
    : theme.palette.orange50,
  border: `1px solid ${
    $allSuccess ? theme.palette.green500 : theme.palette.orange500
  }`,
  marginBottom: theme.spacing(2),
}));

const ResultText = styled('div')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.textPrimaryBlack,
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  width: 24,
  height: 24,
  flexShrink: 0,
  path: {
    stroke: theme.palette.green500,
  },
  circle: {
    stroke: theme.palette.green500,
  },
}));

const WarningIcon = styled(WarningTriangleIcon)(({ theme }) => ({
  width: 24,
  height: 24,
  flexShrink: 0,
  path: {
    stroke: theme.palette.orange500,
  },
}));

export default PreviewPhase;
